import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

/**
 * Service Profitability Ranking Component
 * Shows top/bottom performing services by various metrics
 */
export default function ServiceProfitability({ dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('revenue'); // 'revenue', 'gpm', 'net_margin'

  useEffect(() => {
    fetchProfitabilityData();
  }, [dateRange]);

  const fetchProfitabilityData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/advanced/service-profitability`;
      
      if (dateRange?.startDate) {
        url += `?start_date=${dateRange.startDate}`;
      }
      if (dateRange?.endDate) {
        url += `${dateRange?.startDate ? '&' : '?'}end_date=${dateRange.endDate}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching profitability data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.rankings) return null;

  const rankings = data.rankings[`by_${viewMode}`] || [];
  const topServices = rankings.slice(0, 10);

  const chartData = topServices.map((service) => ({
    name: service.service_name.length > 15 
      ? service.service_name.substring(0, 15) + '...' 
      : service.service_name,
    value: viewMode === 'revenue' 
      ? service.total_gross_revenue / 1000 
      : service[viewMode],
  }));

  const getColor = (index) => {
    const colors = [
      '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
      '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-bold text-slate-900">Service Profitability Ranking</h3>
        </div>
        
        {/* View Mode Selector */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('revenue')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'revenue'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Revenue
          </button>
          <button
            onClick={() => setViewMode('gpm')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'gpm'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            GPM
          </button>
          <button
            onClick={() => setViewMode('net_margin')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'net_margin'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Net Margin
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Total Services</p>
          <p className="text-2xl font-bold text-blue-900">{data.summary.total_services}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-green-900">
            ${(data.summary.total_revenue / 1000).toFixed(0)}K
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Avg GPM</p>
          <p className="text-2xl font-bold text-purple-900">
            {data.summary.avg_gpm.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" stroke="#64748b" style={{ fontSize: '12px' }} />
            <YAxis 
              type="category" 
              dataKey="name" 
              stroke="#64748b" 
              style={{ fontSize: '11px' }} 
              width={120}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value) => [
                viewMode === 'revenue' ? `$${value.toFixed(0)}K` : `${value.toFixed(2)}%`,
                viewMode === 'revenue' ? 'Revenue' : viewMode === 'gpm' ? 'GPM' : 'Net Margin'
              ]}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(index)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Services Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-3 font-semibold text-slate-600">Rank</th>
              <th className="text-left py-2 px-3 font-semibold text-slate-600">Service</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Scenarios</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Revenue</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">GPM</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Net Margin</th>
            </tr>
          </thead>
          <tbody>
            {topServices.map((service, index) => (
              <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-slate-200 text-slate-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                </td>
                <td className="py-2 px-3 font-medium text-slate-900">{service.service_name}</td>
                <td className="py-2 px-3 text-right text-slate-600">{service.scenario_count}</td>
                <td className="py-2 px-3 text-right font-medium text-green-700">
                  ${(service.total_gross_revenue / 1000).toFixed(1)}K
                </td>
                <td className="py-2 px-3 text-right font-medium text-blue-700">
                  {service.gpm.toFixed(1)}%
                </td>
                <td className="py-2 px-3 text-right font-medium text-purple-700">
                  {service.net_margin.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
